export const cesiumConfig = {
  // Default map settings
  defaultMapStyle: 'satellite-streets-v12',
  defaultSceneMode: 3,

  // Default drone position (when no telemetry is available)
  defaultDronePosition: {
    longitude: -0.87760356,
    latitude: 51.40010493,
    altitude: 200.0,
  },
  // defaultDronePosition: {
  //   longitude: -2.825126905328803,
  //   latitude: 51.951778595826624,
  //   altitude: 200.0,
  // },
  // Drone model settings. `uri` is the default multirotor model; per-device
  // overrides live in `droneModelUriByDevice` (FLY3D-965).
  droneModel: {
    uri: '/models/drone/CesiumDrone6.glb',
    minimumPixelSize: 48,
    maximumScale: 20000,
    scale: 1.0,
    runAnimations: true,
    clampAnimations: true,
    incrementallyLoadTextures: true,
    shadows: true,
    silhouetteSize: 0.0,
  },

  // Per-device model overrides (uri + optional size); anything not listed
  // falls back to the default multirotor `droneModel` above.
  droneModelByDevice: {
    IDI_QUADPLANE_RC: {
      uri: '/models/drone/quadplane.glb',
      minimumPixelSize: 96,
      scale: 1.4,
    },
  } as Record<string, { uri: string; minimumPixelSize?: number; scale?: number }>,

  // Flight Story replay settings (FLY3D-1087). The replay substitutes the
  // dashboard's own drone model for whatever the CZML points at and tints it,
  // so the airframe reads as the right machine while staying distinguishable
  // from the live aircraft.
  flightStory: {
    /**
     * How the replayed aircraft is drawn.
     *
     * 'sphere' — a plain sphere following the track. The replay is about where
     *            the aircraft went, not what it looks like, and a sphere reads
     *            the same from every angle. It also costs nothing to render
     *            and never fails to load.
     * 'model'  — the dashboard's own drone model (`getDroneModel`, per-device
     *            override included), so replay matches the live view.
     */
    aircraftRepresentation: 'model' as 'sphere' | 'model',
    /**
     * Sphere radius in METRES — roughly airframe scale, matching the size the
     * drone model actually occupied in the world.
     *
     * The model also carried `minimumPixelSize: 48`, so it never shrank below
     * 48px however far away the camera was. An ellipsoid has no such floor: at
     * this radius it is a couple of pixels from the default 600m replay range,
     * which is to say invisible. `markerPixelSize` below restores that floor.
     */
    sphereRadiusMeters: 1,
    /**
     * Beyond `markerSwitchDistanceMeters` the sphere is swapped for a
     * screen-space point of this size, so the aircraft stays findable at any
     * zoom without inflating the sphere — which would only obscure the track
     * it is meant to be following.
     */
    markerPixelSize: 10,
    markerSwitchDistanceMeters: 300,
    sphereColor: '#0ada45',
    sphereAlpha: 0.9,
    /**
     * Replace the CZML's own model with the dashboard's standard drone model
     * (`getDroneModel`, including its per-device override), so a replayed
     * aircraft looks exactly like the live one. Only consulted when
     * `aircraftRepresentation` is 'model'.
     */
    useDashboardModel: true,
    /**
     * Tint the replay aircraft, so a replayed machine is distinguishable at a
     * glance from the live one even though both now use the same model. Off
     * renders it in its normal dashboard appearance. The route and trail
     * colours below are unaffected — those distinguish the two paths and are
     * not about the aircraft.
     */
    tintAircraft: true,
    aircraftColor: '#0ada45',
    // MIX blend amount: 0 leaves the model untouched, 1 flattens it to a flat
    // silhouette. Mid-range keeps the model's shading legible under the tint.
    aircraftColorBlendAmount: 0.65,
    // Two paths are on screen at once and must not be confused:
    //
    //  - the ROUTE: the whole flight, drawn before playback starts, so the
    //    operator sees where it went before watching it fly. White and narrow —
    //    it is context, not the subject, and white reads against both terrain
    //    and satellite imagery without competing with the green trail.
    //  - the TRAIL: the aircraft's own path graphic, which draws over the route
    //    as the replay progresses. Bright, wider, and the same green as the
    //    tinted aircraft so the moving parts read as one thing.
    routeColor: '#ffffff',
    routeAlpha: 0.65,
    routeWidth: 3,
    trailColor: '#0ada45',
    trailAlpha: 0.95,
    trailWidth: 5,
    // Replay camera. Pulled further back and steeper than the live dashboard's
    // tracking camera: a replay is watched to understand where the aircraft
    // went, which needs the surrounding ground in frame, whereas the live view
    // is flown from close in.
    cameraPitchDegrees: -55,
    cameraRangeMeters: 600,
    // Selecting a flight flies the camera to frame its whole route. This is a
    // one-off move, not the continuous tracking above: it ends with the camera
    // free, so the operator can pan and zoom from there.
    flyToDurationSeconds: 1.5,
    flyToPitchDegrees: -50,
    /**
     * Where the replay aircraft's attitude comes from.
     *
     * 'auto'     — the CZML's own orientation when it has one, otherwise derive
     *              it from the direction of travel.
     * 'czml'     — always the CZML's, even if absent (the model then sits at
     *              Cesium's default orientation, which looks wrong).
     * 'velocity' — always derived from travel direction, ignoring the CZML.
     *
     * The override exists to settle "is the attitude bad data or bad
     * rendering?" empirically: if 'velocity' looks right and 'czml' does not,
     * the CZML's orientation is the problem.
     */
    orientationSource: 'velocity' as 'auto' | 'czml' | 'velocity',
    // Positions sampled from the CZML to build that polyline. Enough to keep
    // the shape faithful, capped so a multi-hour flight cannot stall the load.
    routeSamples: 1500,
  },

  // Drone path settings
  // trailTime: default when no mission (seconds of history to show)
  // maxTrailTime: cap when using mission-based dynamic trail (avoids huge memory use)
  // trailTimeBuffer: extra seconds added to mission duration so path extends past mission end
  dronePath: {
    leadTime: 0,
    trailTime: 300,
    maxTrailTime: 14400, // 4 hours cap for mission-based trail
    trailTimeBuffer: 300, // 5 min buffer after mission end
    width: 5,
    resolution: 60,
    glowPower: 0.3,
    taperPower: 0.3,
    color: [255, 218, 185], // PALEGOLDENROD
  },

  // Camera tracking settings
  cameraTracking: {
    pitchAngle: 30, // degrees
    distance: 100.0,
  },

  // FPV settings
  fpv: {
    height: 400,
    borderColor: 'var(--idi-shade2-50)',
  },

  // Error handling settings
  errorHandling: {
    threshold: 5,
    resetTime: 30000, // 30 seconds
    errorDisplayDuration: 10000, // 10 seconds
  },

  // Animation settings
  animation: {
    propellerRotationSpeed: 3.0, // rotations per second
    modelCheckInterval: 500, // ms
    modelCheckTimeout: 10000, // ms
  },

  // Terrain sampling
  terrainSampling: {
    throttleInterval: 1000, // ms
  },
}

/**
 * Resolve the model config (uri + size) for a device type, falling back to the
 * default multirotor model when the device has no specific model (FLY3D-965).
 */
export function getDroneModel(deviceType?: string | null): {
  uri: string
  minimumPixelSize: number
  scale: number
} {
  const base = {
    uri: cesiumConfig.droneModel.uri,
    minimumPixelSize: cesiumConfig.droneModel.minimumPixelSize,
    scale: cesiumConfig.droneModel.scale,
  }
  const override = deviceType ? cesiumConfig.droneModelByDevice[deviceType] : undefined
  return override ? { ...base, ...override } : base
}
